/**
 * Aggregates data based on a provided key.
 *
 * @param {Array} data - The array of data objects to be aggregated.
 * @param {String} aggregateKey - The key on which to aggregate the data.
 * @param {String} operation - The operation to perform ('sum' or 'average'). Default is 'sum'.
 * @returns {Array} - The aggregated data array.
 */
export function aggregateBy(data, aggregateKey, operation = "sum") {
  const result = data?.reduce((accumulator, currentItem) => {
    const existingItem = accumulator.find(
      (item) => item[aggregateKey] === currentItem[aggregateKey]
    );

    if (existingItem) {
      Object.keys(currentItem).forEach((key) => {
        if (key !== aggregateKey && typeof currentItem[key] === "number") {
          existingItem[key] = (existingItem[key] || 0) + currentItem[key];
          existingItem[`${key}_count`] = (existingItem[`${key}_count`] || 0) + 1;
        }
      });
    } else {
      accumulator.push({
        ...currentItem,
        ...Object.fromEntries(
          Object.keys(currentItem)
            .filter((key) => typeof currentItem[key] === "number")
            .map((key) => [`${key}_count`, 1])
        ),
      });
    }

    return accumulator;
  }, []);

  if (operation === "average") {
    result?.forEach((item) => {
      Object.keys(item).forEach((key) => {
        if (key.endsWith("_count")) {
          const originalKey = key.slice(0, -6);
          if (item[originalKey] !== undefined) {
            item[originalKey] /= item[key];
          }
          delete item[key];
        }
      });
    });
  }

  return result;
}

/**
 * Orders data based on a provided key and order.
 *
 * @param {Array} data - The array of data objects to be ordered.
 * @param {String} key - The key on which to order the data.
 * @param {String} order - The order in which to sort the data ('asc' or 'desc').
 * @returns {Array} - The ordered data array.
 */
export function orderBy(data, key, order = "asc") {
  if (order !== "asc" && order !== "desc") {
    throw new TypeError('The third argument should be a string: "asc" or "desc"');
  }

  return data?.sort((a, b) => {
    if (a[key] < b[key]) {
      return order === "asc" ? -1 : 1;
    }
    if (a[key] > b[key]) {
      return order === "asc" ? 1 : -1;
    }
    return 0;
  });
}

/**
 * This function aggregates an array of data objects based on the specified granularity
 * and performs operations (sum or average) on the specified keys.
 *
 * @param {Array} data - An array of data objects that needs to be aggregated.
 * @param {String} granularity - The level of granularity for the aggregation
 *                               ('MIN15', 'HOUR', 'DAY', 'MONTH', or 'YEAR').
 * @param {Array} aggregationSettings - An array of objects where each object specifies
 *                                      the key to be aggregated and the operation
 *                                      (sum or average) to be performed on that key.
 *
 * @returns {Array} - The aggregated data array.
 */
export function aggregateByGranularity(data, granularity, aggregationSettings) {
  const counts = {};

  return data?.reduce((accumulator, currentItem) => {
    const date = new Date(currentItem.datetime_timestamp);
    const day = date.getDate();
    const month = date.getMonth();
    const year = date.getFullYear();

    let index;
    switch (granularity) {
      case "MIN15":
        return data;
      case "HOUR":
        index = accumulator.findIndex(
          (item) =>
            new Date(item.datetime_timestamp).getHours() === date.getHours() &&
            item.day === day &&
            item.month === month &&
            item.year === year
        );
        break;
      case "DAY":
        index = accumulator.findIndex(
          (item) => item.day === day && item.month === month && item.year === year
        );
        break;
      case "MONTH":
        index = accumulator.findIndex((item) => item.month === month && item.year === year);
        break;
      case "YEAR":
        index = accumulator.findIndex((item) => item.year === year);
        break;
      default:
        return data;
    }

    if (index !== -1) {
      // Existing item, perform aggregation
      const existingItem = accumulator[index];
      aggregationSettings.forEach(({ aggregationKey, operation }) => {
        if (operation === "sum" && typeof currentItem[aggregationKey] === "number") {
          existingItem[aggregationKey] =
            (existingItem[aggregationKey] || 0) + currentItem[aggregationKey];
        } else if (operation === "average" && typeof currentItem[aggregationKey] === "number") {
          existingItem[aggregationKey] =
            (existingItem[aggregationKey] * (counts[index][aggregationKey] || 0) +
              currentItem[aggregationKey]) /
            ((counts[index][aggregationKey] || 0) + 1);
          counts[index][aggregationKey] = (counts[index][aggregationKey] || 0) + 1;
        }
      });
    } else {
      // New item, add to accumulator
      accumulator.push({
        datetime_timestamp: currentItem.datetime_timestamp,
        day,
        month,
        year,
        ...currentItem,
      });
      counts[accumulator.length - 1] = {};
      aggregationSettings.forEach(({ aggregationKey, operation }) => {
        if (operation === "average" && typeof currentItem[aggregationKey] === "number") {
          counts[accumulator.length - 1][aggregationKey] = 1;
        }
      });
    }

    return accumulator;
  }, []);
}

export default {
  aggregateBy,
  orderBy,
};
